/**
 * Unified API Service for SAUBHAGYA Web Admin
 * Connects to all 6 microservices with JWT authentication
 *
 * Service URLs come from serviceConfig.ts (single source of truth).
 * To change a service URL/port, update ONLY the .env file.
 */

import { microservicesClient } from './microservices';
import { getServiceUrl } from '../config/serviceConfig';

// API Configuration
const API_CONFIG = {
  timeout: 15000,
  retries: 3,
  retryDelay: 1000,
};

// Authentication Management
class AuthManager {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('saubhagya_jwt_token', token);
    } else {
      localStorage.removeItem('saubhagya_jwt_token');
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('saubhagya_jwt_token');
    }
    return this.token;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  clear() {
    this.token = null;
    localStorage.removeItem('saubhagya_jwt_token');
  }
}

export const authManager = new AuthManager();

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Data Types
export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  permissions: string[];
}

export interface Cattle {
  id: string;
  rfidTag: string;
  name: string;
  breed: string;
  age: number;
  weight: number;
  health: 'healthy' | 'sick' | 'recovering' | 'vaccination_due';
  ownerId: string;
  ownerName: string;
  location: {
    latitude: number;
    longitude: number;
    timestamp: number;
    address?: string;
  };
  totalDungCollected: number;
  lastDungCollection?: number;
  isActive: boolean;
  photoUrl?: string;
  createdAt: number;
  updatedAt: number;
}

export interface Transaction {
  id: string;
  type: 'dung_collection' | 'biogas_sale' | 'payment';
  cattleId?: string;
  amount: number;
  weight?: number;
  timestamp: number;
  status: 'pending' | 'completed' | 'cancelled';
  description: string;
}

export interface DashboardStats {
  totalCattle: number;
  activeCattle: number;
  totalTransactions: number;
  totalRevenue: number;
  biogasProduced: number;
  dungCollected: number;
}

// Core API Service
class ApiService {
  // Authentication APIs (Auth Service - Port 8081)
  async login(phone: string, password: string): Promise<ApiResponse<{ user: User; token: string }>> {
    try {
      // Auth service URL from serviceConfig (single source of truth)
      const authServiceUrl = getServiceUrl('auth');

      // Direct login to auth service - send phone as-is
      const loginResponse = await fetch(`${authServiceUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone,
          password
        }),
      });

      if (!loginResponse.ok) {
        let errorData = {};
        let errorMessage = 'Login failed. Please try again.';

        try {
          errorData = await loginResponse.json();
        } catch (e) {
          // Response is not JSON
        }

        // Handle different error response formats
        if (loginResponse.status === 423) {
          // Account locked
          const lockoutMinutes = errorData.lockoutDurationMinutes || 30;
          errorMessage = errorData.message || `Account is locked due to too many failed attempts. Please try again after ${lockoutMinutes} minutes.`;
        } else if (loginResponse.status === 401) {
          // Unauthorized - Invalid credentials
          errorMessage = errorData.message || 'Invalid credentials. Please check your phone number and password.';
        } else if (loginResponse.status === 400) {
          // Bad request
          errorMessage = errorData.message || 'Invalid request. Please check your input.';
        } else {
          // Other errors
          errorMessage = errorData.message || errorData.error || 'Login failed. Please try again.';
        }

        console.error('🔐 [API] Login failed:', {
          status: loginResponse.status,
          errorData,
          errorMessage
        });

        throw new Error(errorMessage);
      }

      const responseData = await loginResponse.json();

      console.log('🔐 [API] Raw login response received:', {
        hasAccessToken: !!responseData.accessToken,
        hasUserProfile: !!responseData.userProfile,
        hasData: !!responseData.data,
        responseKeys: Object.keys(responseData)
      });

      // Extract user and token from response
      // Response structure: { accessToken, refreshToken, userProfile: { id, phone, name, email, roles, permissions } }
      const userProfile = responseData.userProfile || responseData.data || {};

      console.log('🔐 [API] Extracted userProfile:', {
        id: userProfile.id,
        phone: userProfile.phone,
        name: userProfile.name,
        email: userProfile.email,
        nameFieldExists: 'name' in userProfile,
        nameIsNullOrUndefined: userProfile.name === null || userProfile.name === undefined,
        nameValue: userProfile.name
      });

      // CRITICAL: Use name from userProfile - NEVER use phone as fallback
      // Backend is required to provide valid name. If empty, throw error instead of creating fallback
      let finalName = userProfile.name;
      if (!finalName || finalName.trim() === '') {
        console.error('❌ [CRITICAL ERROR] Backend returned user without valid name!');
        console.error('This indicates a backend data integrity issue.');
        console.error('User profile from backend:', userProfile);
        throw new Error('Backend returned user without valid name - please contact support');
      }

      const user: User = {
        id: userProfile.id || userProfile.externalId || '1',
        email: userProfile.email || '',
        name: finalName,  // Use validated name field
        role: userProfile.roles?.[0] || 'user',
        permissions: userProfile.permissions || []
      };

      console.log('🔐 [API] Final mapped user:', {
        id: user.id,
        phone: phone,
        name: user.name,
        email: user.email,
        role: user.role
      });

      const jwtToken = responseData.accessToken || responseData.data?.token || responseData.token;

      // CRITICAL: Token validation - token MUST be a non-empty string
      if (!jwtToken || typeof jwtToken !== 'string' || jwtToken.trim() === '') {
        console.error('❌ [CRITICAL] Auth service did not return a valid JWT token!');
        console.error('Response data keys:', Object.keys(responseData));
        console.error('accessToken:', responseData.accessToken);
        console.error('data:', responseData.data);
        console.error('token:', responseData.token);
        throw new Error('Auth service did not return a valid JWT token - backend configuration issue');
      }

      // Validate JWT format - should have 3 parts separated by dots
      const jwtParts = jwtToken.split('.');
      const isValidJWTFormat = jwtParts.length === 3 && jwtParts.every((part: string) => part.length > 0);

      console.log('✅ [API] Token validation:', {
        tokenLength: jwtToken.length,
        tokenStart: jwtToken.substring(0, 20) + '...',
        hasCorrectFormat: isValidJWTFormat,
        jwtPartsCount: jwtParts.length,
        hasComma: jwtToken.includes(','),
        isJSON: jwtToken.startsWith('{') || jwtToken.startsWith('['),
        startsWithBearer: jwtToken.startsWith('Bearer '),
      });

      if (!isValidJWTFormat) {
        console.error('❌ [CRITICAL] Token does not have valid JWT format (3 dot-separated parts)!');
        console.error('Token preview:', jwtToken.substring(0, 100));
        throw new Error('Auth service returned invalid JWT format - expected 3 dot-separated parts');
      }
      console.log('✅ [API] Valid JWT token received:', {
        tokenLength: jwtToken.length,
        tokenStart: jwtToken.substring(0, 20) + '...'
      });

      // Store token - this will also clear old JWT token from localStorage
      authManager.setToken(jwtToken);

      // CRITICAL: Clear any stale user data from previous logins
      // This prevents old cached user data from being reused
      localStorage.removeItem('lastLoggedInUser');
      localStorage.removeItem('previousUserPhone');

      // Store the current login info for cache validation
      const loginInfo = {
        phone: phone,
        userId: user.id,
        timestamp: Date.now()
      };
      localStorage.setItem('currentLoginInfo', JSON.stringify(loginInfo));

      console.log('✅ [API] Login successful - cleared old cache, stored new user data');
      console.log('✅ [API] Returning user:', { name: user.name, id: user.id, phone: phone });

      return {
        success: true,
        data: { user, token: jwtToken },
        message: 'Login successful'
      };

    } catch (error) {
      console.error('🔐 [API] Login error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed'
      };
    }
  }

  async logout(): Promise<ApiResponse<null>> {
    authManager.clear();
    return { success: true, data: null, message: 'Logged out successfully' };
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    if (!authManager.isAuthenticated()) {
      return { success: false, error: 'Not authenticated' };
    }

    // Mock user data - in real implementation, decode JWT or call user endpoint
    return {
      success: true,
      data: {
        id: '1',
        email: 'admin@saubhagya.com',
        name: 'Admin User',
        role: 'admin',
        permissions: ['cattle:read', 'cattle:write', 'transactions:read', 'dashboard:read']
      }
    };
  }

  // Cattle Management APIs (via Auth Service Gateway)
  async getCattle(): Promise<ApiResponse<Cattle[]>> {
    try {
      const response = await microservicesClient.callService('auth-service', '/auth/api/v1/cattle/list');
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch cattle');
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch cattle'
      };
    }
  }

  async addCattle(cattle: Omit<Cattle, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Cattle>> {
    try {
      const response = await microservicesClient.callService('auth-service', '/auth/api/v1/cattle/store', {
        method: 'POST',
        body: JSON.stringify(cattle)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to add cattle');
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to add cattle'
      };
    }
  }

  // IoT Device Management APIs (IoT Service - Port 8080)
  async getDevices(): Promise<ApiResponse<any[]>> {
    try {
      const response = await microservicesClient.callService('iot-service', '/iot/api/v1/devices');
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch devices');
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch devices'
      };
    }
  }

  async getRFIDTags(): Promise<ApiResponse<any[]>> {
    try {
      const response = await microservicesClient.callService('iot-service', '/iot/api/v1/rfid-tags');
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch RFID tags');
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch RFID tags'
      };
    }
  }

  // Transaction APIs (Transaction Service - Port 8082)
  async getTransactions(): Promise<ApiResponse<Transaction[]>> {
    try {
      const response = await microservicesClient.callService('transaction-service', '/api/v1/transactions');
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch transactions');
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch transactions'
      };
    }
  }

  async addTransaction(transaction: Omit<Transaction, 'id' | 'timestamp'>): Promise<ApiResponse<Transaction>> {
    try {
      const response = await microservicesClient.callService('transaction-service', '/api/v1/transactions', {
        method: 'POST',
        body: JSON.stringify(transaction)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to add transaction');
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to add transaction'
      };
    }
  }

  // Sales & Commerce APIs (Sales Service - Port 8083)
  async getSales(): Promise<ApiResponse<any[]>> {
    try {
      const response = await microservicesClient.callService('sales-service', '/api/commerce/sales');
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch sales');
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch sales'
      };
    }
  }

  async getOrders(): Promise<ApiResponse<any[]>> {
    try {
      const response = await microservicesClient.callService('sales-service', '/api/commerce/orders');
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch orders');
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch orders'
      };
    }
  }

  // Government APIs (Government Service - Port 8085)
  async getGovernmentDashboard(): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('government-service', '/government/api/v1/dashboard');
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch government dashboard');
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch government dashboard'
      };
    }
  }

  async getSchemes(): Promise<ApiResponse<any[]>> {
    try {
      const response = await microservicesClient.callService('government-service', '/government/api/v1/schemes');
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch schemes');
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch schemes'
      };
    }
  }

  // Purification Service APIs (Port 8087)
  // Metrics APIs
  async getPurificationRealtimeMetrics(): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('purification-service', '/api/v1/metrics/realtime');
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch realtime metrics');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch realtime metrics'
      };
    }
  }

  async getPurificationUnitMetrics(unitId: string): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('purification-service', `/api/v1/metrics/unit/${unitId}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch unit metrics');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch unit metrics'
      };
    }
  }

  // Cycle Management APIs
  async getPurificationCycles(params?: { page?: number; size?: number; status?: string }): Promise<ApiResponse<any>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page !== undefined) queryParams.append('page', params.page.toString());
      if (params?.size !== undefined) queryParams.append('size', params.size.toString());
      if (params?.status) queryParams.append('status', params.status);

      const url = `/api/v1/cycles/list${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await microservicesClient.callService('purification-service', url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch cycles');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch cycles'
      };
    }
  }

  async createPurificationCycle(cycleData: any): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('purification-service', '/api/v1/cycles', {
        method: 'POST',
        body: JSON.stringify(cycleData)
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create cycle');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create cycle'
      };
    }
  }

  async getPurificationCycleById(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('purification-service', `/api/v1/cycles/${id}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch cycle');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch cycle'
      };
    }
  }

  async completePurificationCycle(id: string, completionData: any): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('purification-service', `/api/v1/cycles/${id}/complete`, {
        method: 'PUT',
        body: JSON.stringify(completionData)
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to complete cycle');
      }

      // Backend returns success:true if cycle was completed, even if response.success field is false
      // Check if the cycle status is COMPLETED or if message indicates success
      if (data.data?.status === 'COMPLETED' || data.message?.includes('successfully')) {
        return { success: true, data: data.data };
      }

      return { success: true, data: data.data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to complete cycle'
      };
    }
  }

  // Purification Units APIs
  async getPurificationUnits(params?: { page?: number; size?: number; status?: string }): Promise<ApiResponse<any>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page !== undefined) queryParams.append('page', params.page.toString());
      if (params?.size !== undefined) queryParams.append('size', params.size.toString());
      if (params?.status) queryParams.append('status', params.status);

      const url = `/api/v1/metrics/units${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await microservicesClient.callService('purification-service', url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch purification units');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch purification units'
      };
    }
  }

  // Quality Control APIs
  async submitQualityTest(testData: any): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('purification-service', '/api/v1/quality/tests', {
        method: 'POST',
        body: JSON.stringify(testData)
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit quality test');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to submit quality test'
      };
    }
  }

  async getQualityTests(params?: { page?: number; size?: number }): Promise<ApiResponse<any>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page !== undefined) queryParams.append('page', params.page.toString());
      if (params?.size !== undefined) queryParams.append('size', params.size.toString());

      const url = `/api/v1/quality/tests${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await microservicesClient.callService('purification-service', url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch quality tests');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch quality tests'
      };
    }
  }

  async getComplianceStatus(): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('purification-service', '/api/v1/quality/compliance');
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch compliance status');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch compliance status'
      };
    }
  }

  // Inventory APIs
  async getPurificationInventory(): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('purification-service', '/api/v1/inventory');
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch inventory');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch inventory'
      };
    }
  }

  async createInventoryTransfer(transferData: any): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('purification-service', '/api/v1/inventory/transfer', {
        method: 'POST',
        body: JSON.stringify(transferData)
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create inventory transfer');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create inventory transfer'
      };
    }
  }

  async getBatchTracking(batchId: string): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('purification-service', `/api/v1/inventory/batch/${batchId}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch batch tracking');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch batch tracking'
      };
    }
  }

  // Slurry Management APIs
  async getSlurryOutputs(): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('purification-service', '/api/v1/slurry/outputs');
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch slurry outputs');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch slurry outputs'
      };
    }
  }

  async createSlurryOutput(slurryData: any): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('purification-service', '/api/v1/slurry/outputs', {
        method: 'POST',
        body: JSON.stringify(slurryData)
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create slurry output');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create slurry output'
      };
    }
  }

  async gradeSlurry(gradingData: any): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('purification-service', '/api/v1/slurry/grading', {
        method: 'POST',
        body: JSON.stringify(gradingData)
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to grade slurry');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to grade slurry'
      };
    }
  }

  // Maintenance APIs
  async createMaintenanceSchedule(scheduleData: any): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('purification-service', '/api/v1/maintenance/schedule', {
        method: 'POST',
        body: JSON.stringify(scheduleData)
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create maintenance schedule');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create maintenance schedule'
      };
    }
  }

  async getMaintenanceSchedules(unitId?: string): Promise<ApiResponse<any>> {
    try {
      const url = unitId
        ? `/api/v1/maintenance/unit/${unitId}/schedules`
        : '/api/v1/maintenance/schedules';
      const response = await microservicesClient.callService('purification-service', url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch maintenance schedules');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch maintenance schedules'
      };
    }
  }

  async createWorkOrder(workOrderData: any): Promise<ApiResponse<any>> {
    try {
      const response = await microservicesClient.callService('purification-service', '/api/v1/maintenance/work-orders', {
        method: 'POST',
        body: JSON.stringify(workOrderData)
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create work order');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create work order'
      };
    }
  }

  async getMaintenanceHistory(unitId: string, params?: { days?: number }): Promise<ApiResponse<any>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.days) queryParams.append('days', params.days.toString());

      const url = `/api/v1/maintenance/unit/${unitId}/history${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await microservicesClient.callService('purification-service', url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch maintenance history');
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch maintenance history'
      };
    }
  }

  // Health Check for all services
  async healthCheck(): Promise<ApiResponse<Record<string, boolean>>> {
    try {
      const healthResults = await microservicesClient.healthCheck();
      return {
        success: true,
        data: healthResults,
        message: 'Health check completed'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Health check failed'
      };
    }
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;

// ============================================
// MAINTENANCE MANAGEMENT API
// ============================================

const PURIFICATION_SERVICE_URL = getServiceUrl('purification');
const COMMERCE_SERVICE_URL = getServiceUrl('commerce');

import type {
  MaintenanceReportCreate,
  MaintenanceReportUpdate,
  MaintenanceReportResponse,
  MaintenanceReportSummary,
  MaintenanceScheduleCreate,
  MaintenanceScheduleUpdate,
  MaintenanceScheduleResponse,
  MaintenanceScheduleSummary,
  TemplateCreate,
  TemplateUpdate,
  TemplateResponse,
  TemplateSummary,
  ReportFilters,
  ScheduleFilters,
  TemplateFilters,
  MaintenanceStatistics,
  Page
} from '../types/maintenance';

/**
 * Maintenance Management API Service
 * Provides comprehensive methods for maintenance operations in purification service
 * Story 3.12: Frontend Maintenance API Methods
 */
export const maintenanceAPI = {
  // ============================================
  // REPORT OPERATIONS (7 methods)
  // ============================================

  /**
   * Create a new maintenance report
   * @param data - Report creation request payload
   * @returns Promise with created report response
   */
  async createReport(data: MaintenanceReportCreate): Promise<ApiResponse<MaintenanceReportResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create maintenance report');
      }

      const result = await response.json();
      toast.success('Maintenance report created successfully', {
        description: `Report for schedule ${data.scheduleId} has been created`
      });

      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create maintenance report';
      toast.error('Report creation failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Update an existing maintenance report
   * @param id - Report ID (UUID)
   * @param data - Report update data
   * @returns Promise with updated report response
   */
  async updateReport(id: string, data: MaintenanceReportUpdate): Promise<ApiResponse<MaintenanceReportResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance/reports/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update maintenance report');
      }

      const result = await response.json();
      toast.success('Maintenance report updated successfully', {
        description: `Report ${id} has been updated`
      });

      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update maintenance report';
      toast.error('Report update failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Complete a maintenance report
   * Changes status to PENDING_VERIFICATION
   * @param id - Report ID (UUID)
   * @returns Promise with completed report response
   */
  async completeReport(id: string): Promise<ApiResponse<MaintenanceReportResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance/reports/${id}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to complete maintenance report');
      }

      const result = await response.json();
      toast.success('Maintenance completed successfully', {
        description: `Report ${id} is now pending verification`
      });

      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete maintenance report';
      toast.error('Report completion failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Verify a maintenance report (supervisor action)
   * Changes status to VERIFIED
   * @param id - Report ID (UUID)
   * @param verifiedBy - Supervisor user ID (UUID)
   * @returns Promise with verified report response
   */
  async verifyReport(id: string, verifiedBy: string): Promise<ApiResponse<MaintenanceReportResponse>> {
    try {
      const token = authManager.getToken();
      const queryParams = new URLSearchParams();
      queryParams.append('verifiedBy', verifiedBy);

      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance/reports/${id}/verify?${queryParams.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to verify maintenance report');
      }

      const result = await response.json();
      toast.success('Maintenance verified successfully', {
        description: `Report ${id} has been verified`
      });

      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify maintenance report';
      toast.error('Report verification failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get maintenance report by ID
   * @param id - Report ID (UUID)
   * @returns Promise with report details
   */
  async getReportById(id: string): Promise<ApiResponse<MaintenanceReportResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance/reports/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch maintenance report');
      }

      const result = await response.json();
      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch maintenance report';
      toast.error('Failed to load report', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get all maintenance reports with optional filters
   * @param filters - Optional filters (unit, type, status, technician, pagination)
   * @returns Promise with paginated report list
   */
  async getAllReports(filters?: ReportFilters): Promise<ApiResponse<Page<MaintenanceReportSummary>>> {
    try {
      const token = authManager.getToken();
      const queryParams = new URLSearchParams();

      if (filters) {
        if (filters.unitId !== undefined) queryParams.append('unitId', filters.unitId.toString());
        if (filters.type) queryParams.append('type', filters.type);
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.technicianId) queryParams.append('technicianId', filters.technicianId);
        if (filters.page !== undefined) queryParams.append('page', filters.page.toString());
        if (filters.size !== undefined) queryParams.append('size', filters.size.toString());
      }

      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance/reports?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch maintenance reports');
      }

      const result = await response.json();
      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch maintenance reports';
      toast.error('Failed to load reports', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get maintenance reports for a specific schedule
   * NEW DEDICATED ENDPOINT: GET /api/v1/maintenance/reports/schedule/{scheduleId}
   * @param scheduleId - Schedule ID to filter by
   * @param page - Page number (0-indexed)
   * @param size - Items per page
   * @returns Promise with paginated report list for the schedule
   */
  async getReportsBySchedule(scheduleId: number, page: number = 0, size: number = 100): Promise<ApiResponse<Page<MaintenanceReportSummary>>> {
    try {
      const token = authManager.getToken();
      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());

      console.log(`📋 [API] Fetching reports for schedule ID: ${scheduleId}`);

      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance/reports/schedule/${scheduleId}?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error(`❌ [API] Failed to fetch reports for schedule ${scheduleId}:`, errorData);
        throw new Error(errorData.message || `Failed to fetch reports for schedule ${scheduleId}`);
      }

      const result = await response.json();
      console.log(`✅ [API] Successfully fetched ${result.content?.length || 0} reports for schedule ${scheduleId}`);

      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `Failed to fetch reports for schedule ${scheduleId}`;
      console.error(`❌ [API] Error in getReportsBySchedule:`, errorMessage);
      toast.error('Failed to load reports', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get maintenance report statistics
   * @returns Promise with statistics (counts by status and type)
   */
  async getReportStatistics(): Promise<ApiResponse<MaintenanceStatistics>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance/reports/statistics`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch maintenance statistics');
      }

      const result = await response.json();
      return {
        success: true,
        data: result as MaintenanceStatistics
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch maintenance statistics';
      toast.error('Failed to load statistics', {
        description: errorMessage
      });
      throw error;
    }
  },

  // ============================================
  // SCHEDULE OPERATIONS (6 methods)
  // ============================================

  /**
   * Create a new maintenance schedule
   * @param data - Schedule creation request payload
   * @returns Promise with created schedule response
   */
  async createSchedule(data: MaintenanceScheduleCreate): Promise<ApiResponse<MaintenanceScheduleResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance/schedules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create maintenance schedule');
      }

      const result = await response.json();
      toast.success('Maintenance schedule created successfully', {
        description: `Schedule for unit ${data.unitId} has been created`
      });

      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create maintenance schedule';
      toast.error('Schedule creation failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Update an existing maintenance schedule
   * @param id - Schedule ID (UUID)
   * @param data - Schedule update data
   * @returns Promise with updated schedule response
   */
  async updateSchedule(id: string, data: MaintenanceScheduleUpdate): Promise<ApiResponse<MaintenanceScheduleResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance/schedules/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update maintenance schedule');
      }

      const result = await response.json();
      toast.success('Maintenance schedule updated successfully', {
        description: `Schedule ${id} has been updated`
      });

      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update maintenance schedule';
      toast.error('Schedule update failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get all maintenance schedules with optional filters
   * @param filters - Optional filters (unit, type, priority, technician, pagination)
   * @returns Promise with paginated schedule list
   */
  async getAllSchedules(filters?: ScheduleFilters): Promise<ApiResponse<Page<MaintenanceScheduleSummary>>> {
    try {
      const token = authManager.getToken();
      const queryParams = new URLSearchParams();

      if (filters) {
        if (filters.unitId !== undefined) queryParams.append('unitId', filters.unitId.toString());
        if (filters.maintenanceType) queryParams.append('maintenanceType', filters.maintenanceType);
        if (filters.priority) queryParams.append('priority', filters.priority);
        if (filters.assignedTechnicianId) queryParams.append('assignedTechnicianId', filters.assignedTechnicianId);
        if (filters.page !== undefined) queryParams.append('page', filters.page.toString());
        if (filters.size !== undefined) queryParams.append('size', filters.size.toString());
      }

      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance/schedules?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch maintenance schedules');
      }

      const result = await response.json();
      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch maintenance schedules';
      toast.error('Failed to load schedules', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get upcoming maintenance schedules (next 7 days)
   * @returns Promise with list of upcoming schedules
   */
  async getUpcomingSchedules(): Promise<ApiResponse<MaintenanceScheduleSummary[]>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance/schedules/upcoming`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch upcoming schedules');
      }

      const result = await response.json();
      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch upcoming schedules';
      toast.error('Failed to load upcoming schedules', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get overdue maintenance schedules
   * @returns Promise with list of overdue schedules
   */
  async getOverdueSchedules(): Promise<ApiResponse<MaintenanceScheduleSummary[]>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance/schedules/overdue`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch overdue schedules');
      }

      const result = await response.json();
      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch overdue schedules';
      toast.error('Failed to load overdue schedules', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get schedule with linked maintenance report
   * @param id - Schedule ID (UUID)
   * @returns Promise with schedule details including linked report
   */
  async getScheduleWithReport(id: string): Promise<ApiResponse<MaintenanceScheduleResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance/schedules/${id}/report`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch schedule with report');
      }

      const result = await response.json();
      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch schedule with report';
      toast.error('Failed to load schedule', {
        description: errorMessage
      });
      throw error;
    }
  },

  // ============================================
  // TEMPLATE OPERATIONS (6 methods)
  // ============================================

  /**
   * Create a new maintenance template
   * @param data - Template creation request payload
   * @returns Promise with created template response
   */
  async createTemplate(data: TemplateCreate): Promise<ApiResponse<TemplateResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance-templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create maintenance template');
      }

      const result = await response.json();
      toast.success('Maintenance template created successfully', {
        description: `Template ${data.templateCode} has been created`
      });

      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create maintenance template';
      toast.error('Template creation failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Update an existing maintenance template
   * @param id - Template ID (UUID)
   * @param data - Template update data
   * @returns Promise with updated template response
   */
  async updateTemplate(id: string, data: TemplateUpdate): Promise<ApiResponse<TemplateResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance-templates/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update maintenance template');
      }

      const result = await response.json();
      toast.success('Maintenance template updated successfully', {
        description: `Template ${id} has been updated`
      });

      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update maintenance template';
      toast.error('Template update failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Duplicate an existing maintenance template
   * @param id - Template ID (UUID) to duplicate
   * @param newTemplateCode - New template code for the duplicate
   * @returns Promise with duplicated template response
   */
  async duplicateTemplate(id: string, newTemplateCode: string): Promise<ApiResponse<TemplateResponse>> {
    try {
      const token = authManager.getToken();
      const queryParams = new URLSearchParams();
      queryParams.append('newTemplateCode', newTemplateCode);

      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance-templates/${id}/clone?${queryParams.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to duplicate maintenance template');
      }

      const result = await response.json();
      toast.success('Maintenance template duplicated successfully', {
        description: `New template ${newTemplateCode} has been created`
      });

      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to duplicate maintenance template';
      toast.error('Template duplication failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Deactivate a maintenance template (soft delete)
   * @param id - Template ID (UUID)
   * @returns Promise with void (template deactivated)
   */
  async deactivateTemplate(id: string): Promise<ApiResponse<void>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance-templates/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to deactivate maintenance template');
      }

      toast.success('Maintenance template deactivated successfully', {
        description: `Template ${id} has been deactivated`
      });

      return {
        success: true
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to deactivate maintenance template';
      toast.error('Template deactivation failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get maintenance template by ID
   * @param id - Template ID (UUID)
   * @returns Promise with template details
   */
  async getTemplateById(id: string): Promise<ApiResponse<TemplateResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance-templates/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch maintenance template');
      }

      const result = await response.json();
      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch maintenance template';
      toast.error('Failed to load template', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get all maintenance templates with optional filters
   * @param filters - Optional filters (equipment type, maintenance type, priority, active status, search query, pagination)
   * @returns Promise with paginated template list
   */
  async getAllTemplates(filters?: TemplateFilters): Promise<ApiResponse<Page<TemplateSummary>>> {
    try {
      const token = authManager.getToken();
      const queryParams = new URLSearchParams();

      if (filters) {
        // Handle search query first (uses different endpoint)
        if (filters.query) {
          queryParams.append('query', filters.query);
          if (filters.page !== undefined) queryParams.append('page', filters.page.toString());
          if (filters.size !== undefined) queryParams.append('size', filters.size.toString());

          const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance-templates/search?${queryParams.toString()}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` })
            }
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to search maintenance templates');
          }

          const result = await response.json();
          return {
            success: true,
            data: result
          };
        }

        // Regular filters
        if (filters.equipmentType) queryParams.append('equipmentType', filters.equipmentType);
        if (filters.maintenanceType) queryParams.append('maintenanceType', filters.maintenanceType);
        if (filters.priority) queryParams.append('priority', filters.priority);
        if (filters.page !== undefined) queryParams.append('page', filters.page.toString());
        if (filters.size !== undefined) queryParams.append('size', filters.size.toString());

        // Handle active filter (uses different endpoint)
        if (filters.isActive === true) {
          const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance-templates/active?${queryParams.toString()}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` })
            }
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to fetch active templates');
          }

          const result = await response.json();
          return {
            success: true,
            data: result
          };
        }
      }

      // Default: get all templates
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/maintenance-templates?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch maintenance templates');
      }

      const result = await response.json();
      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch maintenance templates';
      toast.error('Failed to load templates', {
        description: errorMessage
      });
      throw error;
    }
  }
};

// ============================================
// CYCLE MANAGEMENT API
// ============================================

import type {
  CycleCreateRequest,
  CycleCompleteRequest,
  CycleResponse,
  CycleComparison,
  CycleStatistics,
  IoTMetrics,
  PagedCycleResponse,
  ApiCycleResponse
} from '../types/cycle';

/**
 * Cycle Management API Service
 * Provides comprehensive methods for purification cycle operations
 */
export const cycleAPI = {
  // ============================================
  // CREATE OPERATIONS
  // ============================================

  /**
   * Create a new purification cycle
   * @param data - Cycle creation request payload
   * @returns Promise with created cycle response
   */
  async create(data: CycleCreateRequest): Promise<ApiResponse<CycleResponse>> {
    try {
      console.log('=== API: create called ===');
      console.log('API received data:', data);
      console.log('API data.unitId:', data.unitId);
      console.log('API data keys:', Object.keys(data));
      const bodyString = JSON.stringify(data);
      console.log('API JSON stringified body:', bodyString);

      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/cycles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: bodyString
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create cycle');
      }

      const result = await response.json();

      // Extract data from ApiResponse wrapper if present
      const cycleData = result.data || result;

      toast.success('Cycle created successfully', {
        description: `Cycle ${cycleData.cycleCode} has been created`
      });

      return {
        success: true,
        data: cycleData
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create cycle';
      toast.error('Cycle creation failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Complete a purification cycle with final metrics
   * @param id - Cycle ID (UUID string)
   * @param data - Completion data (output metrics, quality grade)
   * @returns Promise with completed cycle response
   */
  async complete(id: string, data: CycleCompleteRequest): Promise<ApiResponse<CycleResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/cycles/${id}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to complete cycle');
      }

      const result = await response.json();
      const cycleData = result.data || result;

      toast.success('Cycle completed successfully', {
        description: `Cycle ${cycleData.cycleCode} has been marked as completed with grade ${cycleData.qualityGrade}`
      });

      return {
        success: true,
        data: cycleData
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete cycle';
      toast.error('Cycle completion failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  // ============================================
  // READ OPERATIONS
  // ============================================

  /**
   * Get cycle by ID with full details
   * @param id - Cycle ID (UUID string)
   * @returns Promise with cycle details
   */
  async getById(id: string): Promise<ApiResponse<CycleResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/cycles/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch cycle');
      }

      const result = await response.json();
      const cycleData = result.data || result;

      return {
        success: true,
        data: cycleData
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch cycle';
      toast.error('Failed to load cycle', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get all cycles for a specific batch
   * @param batchId - Purification batch ID
   * @param page - Page number (0-indexed)
   * @param size - Page size (default: 20)
   * @returns Promise with paginated cycle list
   */
  async getByBatchId(batchId: string, page: number = 0, size: number = 20): Promise<ApiResponse<PagedCycleResponse>> {
    try {
      const token = authManager.getToken();
      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());

      const response = await fetch(
        `${PURIFICATION_SERVICE_URL}/api/v1/cycles/batch/${batchId}?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch cycles');
      }

      const result = await response.json();

      // Handle Spring Page response structure
      const pagedData = result.data || result;

      return {
        success: true,
        data: {
          content: pagedData.content || [],
          totalElements: pagedData.totalElements || 0,
          totalPages: pagedData.totalPages || 0,
          currentPage: pagedData.number || page,
          pageSize: pagedData.size || size
        }
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch cycles';
      toast.error('Failed to load cycles', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Compare multiple cycles side-by-side
   * @param cycleIds - Array of 2-4 cycle UUIDs to compare
   * @returns Promise with comparison data
   */
  async compare(cycleIds: string[]): Promise<ApiResponse<CycleComparison[]>> {
    try {
      if (cycleIds.length < 2 || cycleIds.length > 4) {
        throw new Error('Must compare 2-4 cycles');
      }

      const token = authManager.getToken();
      const queryParams = new URLSearchParams();
      cycleIds.forEach(id => queryParams.append('ids', id));

      const response = await fetch(
        `${PURIFICATION_SERVICE_URL}/api/v1/cycles/compare?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to compare cycles');
      }

      const result = await response.json();
      const comparisonData = result.data || result;

      return {
        success: true,
        data: comparisonData
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to compare cycles';
      toast.error('Cycle comparison failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get cycle statistics for a date range
   * @param fromDate - Start date
   * @param toDate - End date
   * @returns Promise with aggregated statistics
   */
  async getStatistics(fromDate: Date, toDate: Date): Promise<ApiResponse<CycleStatistics>> {
    try {
      const token = authManager.getToken();
      const queryParams = new URLSearchParams();
      queryParams.append('fromDate', fromDate.toISOString().split('T')[0]); // Format: YYYY-MM-DD
      queryParams.append('toDate', toDate.toISOString().split('T')[0]);

      const response = await fetch(
        `${PURIFICATION_SERVICE_URL}/api/v1/cycles/statistics?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch statistics');
      }

      const result = await response.json();
      const statsData = result.data || result;

      return {
        success: true,
        data: statsData
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch statistics';
      toast.error('Failed to load statistics', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get real-time IoT metrics for a cycle
   * @param id - Cycle ID (UUID string)
   * @returns Promise with IoT sensor readings
   */
  async getIoTMetrics(id: string): Promise<ApiResponse<IoTMetrics>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/cycles/${id}/iot-metrics`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch IoT metrics');
      }

      const result = await response.json();
      const metricsData = result.data || result;

      return {
        success: true,
        data: metricsData
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch IoT metrics';
      toast.error('Failed to load IoT metrics', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get efficiency trend data for charts
   * @param fromDate - Start date
   * @param toDate - End date
   * @param unitId - Optional: Filter by specific purification unit
   * @returns Promise with trend statistics
   */
  async getTrends(fromDate: Date, toDate: Date, unitId?: number): Promise<ApiResponse<CycleStatistics>> {
    try {
      const token = authManager.getToken();

      // Calculate days between dates
      const days = Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));

      const queryParams = new URLSearchParams();
      if (unitId) {
        queryParams.append('unitId', unitId.toString());
      }
      queryParams.append('days', Math.min(days, 365).toString()); // Max 365 days

      const response = await fetch(
        `${PURIFICATION_SERVICE_URL}/api/v1/cycles/trends?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch trends');
      }

      const result = await response.json();
      const trendsData = result.data || result;

      return {
        success: true,
        data: trendsData
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch trends';
      toast.error('Failed to load trends', {
        description: errorMessage
      });
      throw error;
    }
  }
};

// ============================================
// BATCH MANAGEMENT API
// ============================================

import type {
  BatchCreateRequest,
  BatchUpdateRequest,
  CompleteBatchRequest,
  RejectBatchRequest,
  BatchResponse,
  BatchSummary,
  BatchStatistics,
  BatchFilters,
  PagedResponse,
  PurificationCycle
} from '../types/batch';
import { toast } from 'sonner';

/**
 * Batch Management API Service
 * Provides comprehensive methods for batch operations in purification service
 */
export const batchAPI = {
  // ============================================
  // CRUD OPERATIONS
  // ============================================

  /**
   * Create a new batch
   * @param data - Batch creation request payload
   * @returns Promise with created batch response
   */
  async create(data: BatchCreateRequest): Promise<ApiResponse<BatchResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${COMMERCE_SERVICE_URL}/api/v1/inventory/batches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create batch');
      }

      const result = await response.json();
      toast.success('Batch created successfully', {
        description: `Batch ${result.batchCode} has been created`
      });

      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create batch';
      toast.error('Batch creation failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Sync a batch from biogas service
   * @param biogasBatchId - ID of the biogas batch to sync
   * @param receivedByUserId - ID of user receiving the batch
   * @returns Promise with synced batch response
   */
  async sync(biogasBatchId: string, receivedByUserId: number): Promise<ApiResponse<BatchResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/batches/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ biogasBatchId, receivedByUserId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to sync batch');
      }

      const result = await response.json();
      toast.success('Batch synced successfully', {
        description: `Batch ${result.batchCode} has been synchronized`
      });

      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to sync batch';
      toast.error('Batch sync failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Update an existing batch
   * @param id - Batch ID
   * @param data - Partial batch update payload
   * @returns Promise with updated batch response
   */
  async update(id: string, data: BatchUpdateRequest): Promise<ApiResponse<BatchResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/batches/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update batch');
      }

      const result = await response.json();
      toast.success('Batch updated successfully', {
        description: `Batch ${result.batchCode} has been updated`
      });

      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update batch';
      toast.error('Batch update failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Complete a batch
   * @param id - Batch ID
   * @param data - Completion data (actual output, timestamp, notes)
   * @returns Promise with completed batch response
   */
  async complete(id: string, data: CompleteBatchRequest): Promise<ApiResponse<BatchResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/batches/${id}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to complete batch');
      }

      const result = await response.json();
      toast.success('Batch completed successfully', {
        description: `Batch ${result.batchCode} has been marked as completed`
      });

      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete batch';
      toast.error('Batch completion failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Reject a batch
   * @param id - Batch ID
   * @param data - Rejection data (reason, rejected by user ID)
   * @returns Promise with rejected batch response
   */
  async reject(id: string, data: RejectBatchRequest): Promise<ApiResponse<BatchResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/batches/${id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reject batch');
      }

      const result = await response.json();
      toast.success('Batch rejected', {
        description: `Batch ${result.batchCode} has been rejected`
      });

      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reject batch';
      toast.error('Batch rejection failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  // ============================================
  // READ OPERATIONS
  // ============================================

  /**
   * Get batch by ID
   * @param id - Batch ID
   * @returns Promise with batch details
   */
  async getById(id: string): Promise<ApiResponse<BatchResponse>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${COMMERCE_SERVICE_URL}/api/v1/inventory/batches/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch batch');
      }

      const result = await response.json();
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch batch';
      toast.error('Failed to load batch', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get all batches with pagination and filters
   * @param page - Page number (0-indexed)
   * @param size - Page size
   * @param filters - Optional filters (status, priority, grade, dates)
   * @returns Promise with paginated batch list
   */
  async getAll(page: number = 0, size: number = 20, filters?: BatchFilters): Promise<ApiResponse<PagedResponse<BatchSummary>>> {
    try {
      const token = authManager.getToken();
      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());

      if (filters) {
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.priority) queryParams.append('priority', filters.priority);
        if (filters.qualityGrade) queryParams.append('qualityGrade', filters.qualityGrade);
        if (filters.fromDate) queryParams.append('fromDate', filters.fromDate);
        if (filters.toDate) queryParams.append('toDate', filters.toDate);
        if (filters.unitId) queryParams.append('unitId', filters.unitId);
        if (filters.sourceBatchNumber) queryParams.append('sourceBatchNumber', filters.sourceBatchNumber);
      }

      const response = await fetch(`${COMMERCE_SERVICE_URL}/api/v1/inventory/batches?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch batches');
      }

      const result = await response.json();
      return {
        success: true,
        data: result.data || result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch batches';
      toast.error('Failed to load batches', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get batches by status
   * @param status - Batch status
   * @param page - Page number (0-indexed)
   * @param size - Page size
   * @returns Promise with paginated batch list
   */
  async getByStatus(status: string, page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<BatchSummary>>> {
    return this.getAll(page, size, { status: status as any });
  },

  /**
   * Get batches by priority
   * @param priority - Batch priority
   * @param page - Page number (0-indexed)
   * @param size - Page size
   * @returns Promise with paginated batch list
   */
  async getByPriority(priority: string, page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<BatchSummary>>> {
    return this.getAll(page, size, { priority: priority as any });
  },

  /**
   * Get batches by quality grade
   * @param grade - Quality grade
   * @param page - Page number (0-indexed)
   * @param size - Page size
   * @returns Promise with paginated batch list
   */
  async getByGrade(grade: string, page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<BatchSummary>>> {
    return this.getAll(page, size, { qualityGrade: grade as any });
  },

  /**
   * Get batches by purification unit
   * @param unitId - Purification unit ID
   * @param page - Page number (0-indexed)
   * @param size - Page size
   * @returns Promise with paginated batch list
   */
  async getByUnit(unitId: string, page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<BatchSummary>>> {
    return this.getAll(page, size, { unitId });
  },

  /**
   * Get batch statistics for date range
   * @param fromDate - Start date (ISO 8601)
   * @param toDate - End date (ISO 8601)
   * @returns Promise with batch statistics
   */
  async getStatistics(fromDate: Date, toDate: Date): Promise<ApiResponse<BatchStatistics>> {
    try {
      const token = authManager.getToken();
      const queryParams = new URLSearchParams();
      queryParams.append('fromDate', fromDate.toISOString());
      queryParams.append('toDate', toDate.toISOString());

      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/batches/statistics?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch statistics');
      }

      const result = await response.json();
      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch statistics';
      toast.error('Failed to load statistics', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Get purification cycles for a batch
   * @param id - Batch ID
   * @returns Promise with list of purification cycles
   */
  async getCycles(id: string): Promise<ApiResponse<PurificationCycle[]>> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/batches/${id}/cycles`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch cycles');
      }

      const result = await response.json();
      return {
        success: true,
        data: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch cycles';
      toast.error('Failed to load cycles', {
        description: errorMessage
      });
      throw error;
    }
  },

  /**
   * Export batches to CSV
   * @param filters - Optional filters for export
   * @returns Promise with CSV file blob
   */
  async exportCsv(filters?: BatchFilters): Promise<Blob> {
    try {
      const token = authManager.getToken();
      const queryParams = new URLSearchParams();

      if (filters) {
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.priority) queryParams.append('priority', filters.priority);
        if (filters.qualityGrade) queryParams.append('qualityGrade', filters.qualityGrade);
        if (filters.fromDate) queryParams.append('fromDate', filters.fromDate);
        if (filters.toDate) queryParams.append('toDate', filters.toDate);
        if (filters.unitId) queryParams.append('unitId', filters.unitId);
        if (filters.sourceBatchNumber) queryParams.append('sourceBatchNumber', filters.sourceBatchNumber);
      }

      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/batches/export/csv?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export CSV');
      }

      const blob = await response.blob();

      // Trigger browser download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `batches-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Export successful', {
        description: 'Batch data has been exported to CSV'
      });

      return blob;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to export CSV';
      toast.error('Export failed', {
        description: errorMessage
      });
      throw error;
    }
  },

  // ============================================
  // DELETE OPERATION
  // ============================================

  /**
   * Delete a batch (soft delete)
   * @param id - Batch ID
   * @returns Promise with void (204 No Content)
   */
  async deleteBatch(id: string): Promise<void> {
    try {
      const token = authManager.getToken();
      const response = await fetch(`${PURIFICATION_SERVICE_URL}/api/v1/batches/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete batch');
      }

      toast.success('Batch deleted successfully', {
        description: 'The batch has been removed from the system'
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete batch';
      toast.error('Batch deletion failed', {
        description: errorMessage
      });
      throw error;
    }
  }
};